import type { RoutingTarget } from './audience.js';
import type { FieldSet, TicketStatus } from './enums.js';
import type { Principal } from './principal.js';
import { hasWorkingAccess, isPrivileged } from './principal.js';

export const STAGE_TONES = ['neutral', 'gold', 'cyan', 'positive', 'caution', 'critical'] as const;
export type StageTone = (typeof STAGE_TONES)[number];

export const ACTOR_RULES = [
  'anyone_involved',
  'department_member',
  'assignee_only',
  'requester_only',
] as const;
export type ActorRule = (typeof ACTOR_RULES)[number];

export const ACTOR_RULE_LABELS: Readonly<Record<ActorRule, string>> = Object.freeze({
  anyone_involved: 'Anyone who can see the request',
  department_member: 'Members of a chosen department',
  assignee_only: 'Only the current assignee',
  requester_only: 'Only the person who raised it',
});

export interface WorkflowStage {
  readonly id: string;
  readonly slug: string;
  readonly label: string;
  readonly tone: StageTone;
  readonly position: number;
  readonly isInitial: boolean;
  readonly isTerminal: boolean;
  readonly isHidden: boolean;
  readonly resultingStatus: TicketStatus;
}

export interface WorkflowTransition {
  readonly id: string;
  readonly slug: string;
  readonly label: string;
  readonly fromStageId: string;
  readonly toStageId: string;
  readonly actorRule: ActorRule;
  readonly actorDepartmentSlug: string | null;
  readonly forbidSelfService: boolean;
  readonly handOffTarget: RoutingTarget | null;
  readonly position: number;
}

export interface WorkflowDefinition {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly fieldSet: FieldSet;
  readonly stages: readonly WorkflowStage[];
  readonly transitions: readonly WorkflowTransition[];
}

export interface WorkflowTicketFacts {
  readonly stageId: string | null;
  readonly requesterId: string;
  readonly assigneeId: string | null;
  readonly departmentSlug: string;
}

export type WorkflowDenialReason =
  | 'unknown_transition'
  | 'wrong_stage'
  | 'not_authorized'
  | 'self_service_forbidden';

export type WorkflowDecision =
  | { readonly allowed: true; readonly transition: WorkflowTransition; readonly toStage: WorkflowStage }
  | { readonly allowed: false; readonly reason: WorkflowDenialReason };

export const WORKFLOW_DENIAL_MESSAGES: Readonly<Record<WorkflowDenialReason, string>> =
  Object.freeze({
    unknown_transition: 'That action does not exist on this workflow.',
    wrong_stage: 'This request has already moved past that step.',
    not_authorized: 'You are not allowed to make that move.',
    self_service_forbidden: 'You cannot approve or reject your own request.',
  });

export function findStage(
  workflow: WorkflowDefinition,
  stageId: string | null,
): WorkflowStage | undefined {
  if (stageId === null) return undefined;
  return workflow.stages.find((stage) => stage.id === stageId);
}

export function initialStage(workflow: WorkflowDefinition): WorkflowStage | undefined {
  return workflow.stages.find((stage) => stage.isInitial);
}

export function visibleStages(workflow: WorkflowDefinition): readonly WorkflowStage[] {
  return [...workflow.stages]
    .filter((stage) => !stage.isHidden)
    .sort((left, right) => left.position - right.position);
}

function actorSatisfies(
  principal: Principal,
  transition: WorkflowTransition,
  ticket: WorkflowTicketFacts,
): boolean {
  switch (transition.actorRule) {
    case 'anyone_involved':
      return (
        isPrivileged(principal) ||
        ticket.requesterId === principal.id ||
        ticket.assigneeId === principal.id ||
        hasWorkingAccess(principal, ticket.departmentSlug)
      );
    case 'department_member':
      return (
        transition.actorDepartmentSlug !== null &&
        hasWorkingAccess(principal, transition.actorDepartmentSlug)
      );
    case 'assignee_only':
      return isPrivileged(principal) || ticket.assigneeId === principal.id;
    case 'requester_only':
      return isPrivileged(principal) || ticket.requesterId === principal.id;
  }
}

export function decideWorkflowTransition(
  workflow: WorkflowDefinition,
  principal: Principal,
  ticket: WorkflowTicketFacts,
  transitionId: string,
): WorkflowDecision {
  const transition = workflow.transitions.find((entry) => entry.id === transitionId);

  if (transition === undefined) {
    return { allowed: false, reason: 'unknown_transition' };
  }

  if (ticket.stageId === null || transition.fromStageId !== ticket.stageId) {
    return { allowed: false, reason: 'wrong_stage' };
  }

  const toStage = findStage(workflow, transition.toStageId);

  if (toStage === undefined) {
    return { allowed: false, reason: 'unknown_transition' };
  }

  if (!actorSatisfies(principal, transition, ticket)) {
    return { allowed: false, reason: 'not_authorized' };
  }

  if (transition.forbidSelfService && ticket.requesterId === principal.id) {
    return { allowed: false, reason: 'self_service_forbidden' };
  }

  return { allowed: true, transition, toStage };
}

export function availableTransitions(
  workflow: WorkflowDefinition,
  principal: Principal,
  ticket: WorkflowTicketFacts,
): readonly WorkflowTransition[] {
  return [...workflow.transitions]
    .filter((transition) => decideWorkflowTransition(workflow, principal, ticket, transition.id).allowed)
    .sort((left, right) => left.position - right.position);
}

export function transitionToStage(
  workflow: WorkflowDefinition,
  principal: Principal,
  ticket: WorkflowTicketFacts,
  targetStageId: string,
): WorkflowTransition | null {
  const candidates = workflow.transitions.filter(
    (transition) => transition.toStageId === targetStageId,
  );

  for (const candidate of candidates) {
    if (decideWorkflowTransition(workflow, principal, ticket, candidate.id).allowed) {
      return candidate;
    }
  }

  return null;
}

export function isUnrestrictedWorkflow(workflow: WorkflowDefinition): boolean {
  return workflow.transitions.length === 0;
}

export function mayMoveAnywhere(principal: Principal): boolean {
  return isPrivileged(principal);
}

export function allowedTargetStageIds(
  workflow: WorkflowDefinition,
  principal: Principal,
  ticket: WorkflowTicketFacts,
): ReadonlySet<string> {
  if (isUnrestrictedWorkflow(workflow) || mayMoveAnywhere(principal)) {
    return new Set(
      visibleStages(workflow)
        .filter((stage) => stage.id !== ticket.stageId)
        .map((stage) => stage.id),
    );
  }

  return new Set(availableTransitions(workflow, principal, ticket).map((entry) => entry.toStageId));
}
